import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Fit, Product, fitLabels } from '../types/product';
import { useCart } from '../contexts/CartContext';
import { formatINR } from '../utils/currency';
const fits: Fit[] = ['women', 'men'];
export function ProductCard({
  product,
  index
}: {
  product: Product;
  index: number;
}) {
  const {
    addLine
  } = useCart();
  const [fit, setFit] = useState<Fit>('women');
  const [size, setSize] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const sizes = product.sizes[fit];
  const handleFit = (next: Fit) => {
    setFit(next);
    setSize(null);
    setError(false);
  };
  const handleAdd = () => {
    if (!size) {
      setError(true);
      return;
    }
    setError(false);
    addLine(product, size, fit);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 28
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.7,
    delay: index % 3 * 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} className="flex flex-col" data-id="element-187" data-mp="2v5ps9p8cm7wh">
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-parchment" data-id="element-188" data-mp="3gzlvzaan8aoz">
        <AnimatePresence mode="wait" data-id="element-189" data-mp="3cyjyaxwdk6p4">
          <motion.img key={fit} src={product.images[fit]} alt={`${fitLabels[fit]}'s ${product.name} in ${product.colour}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeInOut'
        }} className="absolute inset-0 h-full w-full object-cover" loading="lazy" data-id="element-190" data-mp="3czlgdmbt5tgc" />
        </AnimatePresence>

        <div className="absolute inset-x-0 bottom-0 flex justify-center pb-4" data-id="element-191" data-mp="3qq74bpg4ctl3">
          <div role="radiogroup" aria-label={`Cut for ${product.name} in ${product.colour}`} className="flex items-center gap-5 bg-bone/85 px-5 py-2 backdrop-blur-[2px]" data-id="element-192" data-mp="3qsb5hzmmveho">
            {fits.map(f => <button key={f} type="button" role="radio" aria-checked={fit === f} onClick={() => handleFit(f)} className={`relative pb-0.5 text-[11px] uppercase tracking-[0.18em] transition-colors ${fit === f ? 'text-ink' : 'text-smoke hover:text-ink'}`} data-id="element-193" data-mp="2gt0da8kzm0ok">
                {fitLabels[f]}
                <span className={`absolute inset-x-0 bottom-0 h-px origin-left bg-ink transition-transform duration-500 ${fit === f ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-194" data-mp="1xzcvupvfu580" />
              </button>)}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col pt-6" data-id="element-195" data-mp="1f2lh8tquceaa">
        <div className="flex items-baseline justify-between gap-4" data-id="element-196" data-mp="1f3wleuo77o1x">
          <h3 className="font-display text-xl font-light text-ink" data-id="element-197" data-mp="1k27v95ehtyk5">{product.name}</h3>
          <span className="text-sm font-light text-ink" data-id="element-198" data-mp="10fwg6ngxxt1s">{formatINR(product.price)}</span>
        </div>
        <p className="mt-1 text-right text-[10px] uppercase tracking-[0.18em] text-clay" data-id="element-199" data-mp="vh1pz0gn927n">
          Introductory price
        </p>

        <p className="mt-2 flex items-center gap-2 text-xs font-light tracking-wide text-smoke" data-id="element-200" data-mp="1af3ubcvi1rpy">
          <span className="h-2.5 w-2.5 rounded-full border border-ink/15" style={{
          backgroundColor: product.colourHex
        }} aria-hidden="true" data-id="element-201" data-mp="15dy0fbq3ryeu" />
          {product.colour}
        </p>

        <p className="mt-4 text-sm font-light leading-relaxed text-smoke" data-id="element-202" data-mp="1efe1utyxwq6x">{product.detail}</p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-greige/70 pt-5" data-id="element-203" data-mp="1t40o7fmzii7n">
          {sizes.map(s => {
          const active = size === s;
          return <button key={s} type="button" aria-pressed={active} onClick={() => {
            setSize(s);
            setError(false);
          }} className={`relative pb-0.5 text-[11px] uppercase tracking-[0.18em] transition-colors ${active ? 'text-ink' : 'text-smoke hover:text-ink'}`} data-id="element-204" data-mp="225rykxsgzd7u">
                {s}
                <span className={`absolute inset-x-0 bottom-0 h-px origin-left bg-ink transition-transform duration-300 ${active ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-205" data-mp="a6e0b85m7gf8" />
              </button>;
        })}
        </div>

        <div className="mt-auto pt-6" data-id="element-206" data-mp="3il5zj0sc7964">
          {error && <p role="alert" className="mb-2 text-[11px] tracking-wide text-clay" data-id="element-207" data-mp="3io21d07b2hzu">
              Please select a size.
            </p>}
          <button type="button" onClick={handleAdd} className="eyebrow w-full border border-ink/25 py-3.5 text-ink transition-colors hover:border-ink hover:bg-ink hover:text-bone" data-id="element-208" data-mp="xw8bzme76yxw">
            Add to bag — {fitLabels[fit]}
          </button>
        </div>
      </div>
    </motion.article>;
}